'use client';

import * as React from 'react';
import { Cloud, LockKeyhole, ShieldCheck, Users } from 'lucide-react';
import { motion } from 'motion/react';

const ICONS = { shield: ShieldCheck, cloud: Cloud } as const;
const EMBLEM_ICONS = { lock: LockKeyhole, users: Users } as const;

// Interleaved "atom": inner ring at the cardinal points, outer ring at the
// diagonals - so every 45° there's a chip alternating near/far, evenly spread.
// angle in deg (0 = right, -90 = top); radius in % from center.
const CHIPS = [
  // inner ring - cardinal points
  { label: 'AWS', a: -90, r: 30 },
  { label: 'Stripe', a: 0, r: 30 },
  { label: 'Sentry', a: 90, r: 30 },
  { label: 'HeyGen', a: 180, r: 30 },
  // outer ring - diagonals (fill the gaps)
  { label: 'Vercel', a: -45, r: 44 },
  { label: 'ElevenLabs', a: 45, r: 44 },
  { label: 'OpenAI', a: 135, r: 44 },
  { label: 'PostHog', a: -135, r: 44 }
] as const;

function xy(a: number, r: number): { x: number; y: number } {
  const rad = (a * Math.PI) / 180;
  return { x: 50 + r * Math.cos(rad), y: 50 + r * Math.sin(rad) };
}

function Chip({
  label,
  a,
  r,
  delay
}: {
  label: string;
  a: number;
  r: number;
  delay: number;
}): React.JSX.Element {
  const { x, y } = xy(a, r);
  return (
    <div
      style={{ top: `${y}%`, left: `${x}%` }}
      className="absolute -translate-x-1/2 -translate-y-1/2"
    >
      {/* entrance fade/scale */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.4, delay }}
      >
        {/* the whole pill floats */}
        <motion.div
          className="flex items-center gap-1.5 whitespace-nowrap rounded-full border border-border bg-card py-1.5 pl-2 pr-3 text-[11px] font-semibold text-foreground shadow-md"
          animate={{ y: [0, -6, 0] }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut',
            delay
          }}
        >
          <span className="relative flex size-1.5 shrink-0">
            <motion.span
              className="absolute inline-flex size-full rounded-full bg-primary/60"
              animate={{ scale: [1, 2.2, 1], opacity: [0.6, 0, 0.6] }}
              transition={{
                duration: 2.4,
                repeat: Infinity,
                ease: 'easeOut',
                delay
              }}
            />
            <span className="relative inline-flex size-1.5 rounded-full bg-primary" />
          </span>
          {label}
        </motion.div>
      </motion.div>
    </div>
  );
}

// Central emblem with expanding sonar-style ripples pulsing outward.
export function GlowEmblem({
  variant
}: {
  variant: 'lock' | 'users';
}): React.JSX.Element {
  const Icon = EMBLEM_ICONS[variant];
  return (
    <div className="relative mx-auto flex size-64 items-center justify-center sm:size-72">
      {/* expanding ripples */}
      {[0, 1, 2].map((i) => (
        <motion.span
          key={i}
          className="absolute inset-0 rounded-full bg-primary/10"
          initial={{ scale: 0.36, opacity: 0 }}
          animate={{ scale: 1, opacity: [0, 0.4, 0] }}
          transition={{
            duration: 3.6,
            repeat: Infinity,
            ease: 'easeOut',
            delay: i * 1.2
          }}
        />
      ))}
      {/* static depth layer so there is always a soft halo */}
      <span className="absolute inset-[32%] rounded-full bg-primary/[0.08]" />
      {/* center */}
      <span className="relative flex size-24 items-center justify-center rounded-full bg-card shadow-xl ring-1 ring-primary/20 sm:size-28">
        <Icon className="size-11 text-primary sm:size-12" />
      </span>
    </div>
  );
}

export function OrbitGraphic({
  variant
}: {
  variant: 'shield' | 'cloud';
}): React.JSX.Element {
  const Icon = ICONS[variant];
  return (
    <div className="relative mx-auto aspect-square w-full max-w-[560px]">
      {/* soft radial wash */}
      <span className="absolute inset-[4%] rounded-full bg-gradient-to-b from-primary/[0.07] via-transparent to-transparent" />

      {/* rings */}
      <span className="absolute inset-0 rounded-full border border-border/40" />
      <motion.span
        className="absolute inset-[6%] rounded-full border border-dashed border-primary/25"
        animate={{ rotate: 360 }}
        transition={{ duration: 60, repeat: Infinity, ease: 'linear' }}
      />
      <motion.span
        className="absolute inset-[20%] rounded-full border border-dashed border-primary/20"
        animate={{ rotate: -360 }}
        transition={{ duration: 45, repeat: Infinity, ease: 'linear' }}
      />

      {/* connector spokes: hub → each provider */}
      <svg
        className="pointer-events-none absolute inset-0 h-full w-full"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        aria-hidden
      >
        {CHIPS.map((chip) => {
          const { x, y } = xy(chip.a, chip.r);
          return (
            <line
              key={chip.label}
              x1="50"
              y1="50"
              x2={x}
              y2={y}
              stroke="var(--primary)"
              strokeWidth="0.25"
              strokeOpacity="0.18"
            />
          );
        })}
      </svg>

      {/* center emblem */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
        <motion.span
          className="absolute -inset-5 rounded-full bg-primary/25 blur-2xl"
          animate={{ scale: [1, 1.18, 1], opacity: [0.4, 0.7, 0.4] }}
          transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
        />
        <span className="relative flex size-28 items-center justify-center rounded-full bg-card shadow-xl ring-1 ring-primary/15 sm:size-32">
          <span className="flex size-16 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-lg sm:size-20">
            <Icon className="size-8 sm:size-9" />
          </span>
        </span>
      </div>

      {/* chips */}
      {CHIPS.map((chip, i) => (
        <Chip
          key={chip.label}
          label={chip.label}
          a={chip.a}
          r={chip.r}
          delay={i * 0.18}
        />
      ))}
    </div>
  );
}
